import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  FilterWrapperParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { useGridFilterDisplay } from 'ag-grid-react';
import { SelectField } from '@wk/components-v3-react16';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { useStartTimeFilter } from './useStartTimeFilter';
import type { FilterType, StartTimeFilterModel } from './types';
import styles from './StartTimeFilter.module.scss';

interface StartTimeFilterProperties
  extends CustomFilterDisplayProps<StartTimeFilterModel>,
    FilterWrapperParams {}

const getDefaultDateTime = (): Date => {
  const date = new Date();
  date.setHours(0, 1, 0, 0);
  return date;
};

const getDefaultTime = (): string => '00:01';

const formatDateForDisplay = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCombinedDateTime = (date: Date, timeString: string): string => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const combined = new Date(date);
  combined.setHours(hours ?? 0, minutes ?? 0, 0, 0);
  return combined.toISOString();
};

const parseDateFromModel = (dateString?: string): Date => {
  return dateString ? new Date(dateString) : getDefaultDateTime();
};

const parseTimeFromModel = (dateString?: string): string => {
  return dateString
    ? new Date(dateString).toTimeString().slice(0, 5)
    : getDefaultTime();
};

const StartTimeFilter = ({
  model,
  onStateChange,
  state,
}: StartTimeFilterProperties) => {
  const componentReference = useRef<HTMLDivElement>(null);
  const initialModelSet = useRef(false);

  const afterGuiAttached = useCallback(
    (parameters?: IAfterGuiAttachedParams) => {
      if (!parameters?.suppressFocus) {
        componentReference.current?.focus();
      }
    },
    []
  );

  useGridFilterDisplay({ afterGuiAttached });
  useStartTimeFilter(model);

  const defaultFilterType: FilterType = 'greaterThan';

  const [filterType, setFilterType] = useState<FilterType>(
    state.model?.type || defaultFilterType
  );
  const [startDate, setStartDate] = useState<Date>(
    parseDateFromModel(state.model?.startDate)
  );
  const [startTime, setStartTime] = useState<string>(
    parseTimeFromModel(state.model?.startDate)
  );
  const [endDate, setEndDate] = useState<Date>(
    parseDateFromModel(state.model?.startDateTo)
  );
  const [endTime, setEndTime] = useState<string>(
    parseTimeFromModel(state.model?.startDateTo)
  );

  // Set initial model when filter opens (so Apply works even without interaction)
  useEffect(() => {
    if (!state.model && !initialModelSet.current) {
      const initialModel: StartTimeFilterModel = {
        startDate: getCombinedDateTime(startDate, startTime),
        type: filterType,
      };
      onStateChange({ model: initialModel });
      initialModelSet.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync state when model changes (for Reset button)
  useEffect(() => {
    if (state.model) {
      setFilterType(state.model.type);
      setStartDate(parseDateFromModel(state.model.startDate));
      setStartTime(parseTimeFromModel(state.model.startDate));
      setEndDate(parseDateFromModel(state.model.startDateTo));
      setEndTime(parseTimeFromModel(state.model.startDateTo));
    } else {
      setFilterType(defaultFilterType);
      setStartDate(getDefaultDateTime());
      setStartTime(getDefaultTime());
      setEndDate(getDefaultDateTime());
      setEndTime(getDefaultTime());
    }
  }, [state.model]);

  const handleFilterTypeChange = (newType: FilterType) => {
    setFilterType(newType);

    const newModel: StartTimeFilterModel = {
      startDate: getCombinedDateTime(startDate, startTime),
      ...(newType === 'inRange' && {
        startDateTo: getCombinedDateTime(endDate, endTime),
      }),
      type: newType,
    };

    onStateChange({ model: newModel });
  };

  const handleStartDateChange = useCallback(
    (dates: Date[]) => {
      const newDate = dates[0] || getDefaultDateTime();
      setStartDate(newDate);

      const newModel: StartTimeFilterModel = {
        startDate: getCombinedDateTime(newDate, startTime),
        ...(filterType === 'inRange' && {
          startDateTo: getCombinedDateTime(endDate, endTime),
        }),
        type: filterType,
      };

      onStateChange({ model: newModel });
    },
    [startTime, filterType, endDate, endTime, onStateChange]
  );

  const handleStartTimeChange = useCallback(
    (_: Date[], timeString: string) => {
      setStartTime(timeString);

      const newModel: StartTimeFilterModel = {
        startDate: getCombinedDateTime(startDate, timeString),
        ...(filterType === 'inRange' && {
          startDateTo: getCombinedDateTime(endDate, endTime),
        }),
        type: filterType,
      };

      onStateChange({ model: newModel });
    },
    [startDate, filterType, endDate, endTime, onStateChange]
  );

  const handleEndDateChange = useCallback(
    (dates: Date[]) => {
      const newDate = dates[0] || getDefaultDateTime();
      setEndDate(newDate);

      const newModel: StartTimeFilterModel = {
        startDate: getCombinedDateTime(startDate, startTime),
        ...(filterType === 'inRange' && {
          startDateTo: getCombinedDateTime(newDate, endTime),
        }),
        type: filterType,
      };

      onStateChange({ model: newModel });
    },
    [startDate, startTime, filterType, endTime, onStateChange]
  );

  const handleEndTimeChange = useCallback(
    (_: Date[], timeString: string) => {
      setEndTime(timeString);

      const newModel: StartTimeFilterModel = {
        startDate: getCombinedDateTime(startDate, startTime),
        ...(filterType === 'inRange' && {
          startDateTo: getCombinedDateTime(endDate, timeString),
        }),
        type: filterType,
      };

      onStateChange({ model: newModel });
    },
    [startDate, startTime, filterType, endDate, onStateChange]
  );

  const createPickerOptions = useCallback(
    (isTimePicker: boolean) => ({
      ...(isTimePicker && {
        dateFormat: 'H:i',
        enableTime: true,
        noCalendar: true,
        time_24hr: true,
      }),
      ...(!isTimePicker && {
        dateFormat: 'Y-m-d',
      }),
      allowInput: false,
      clickOpens: true,
      onReady: (_: any, __: any, flatpickrInstance: any) => {
        if (flatpickrInstance?.calendarContainer) {
          flatpickrInstance.calendarContainer.classList.add(
            'ag-custom-component-popup'
          );
          flatpickrInstance.calendarContainer.addEventListener(
            'mousedown',
            (event: Event) => {
              event.stopPropagation();
            }
          );
        }
      },
    }),
    []
  );

  const [startDateOptions, startTimeOptions, endDateOptions, endTimeOptions] =
    useMemo(
      () => [
        createPickerOptions(false),
        createPickerOptions(true),
        createPickerOptions(false),
        createPickerOptions(true),
      ],
      [createPickerOptions]
    );

  const filterTypeOptions = useMemo(
    () => [
      { label: 'Before', value: 'lessThan' },
      { label: 'After', value: 'greaterThan' },
      { label: 'Between', value: 'inRange' },
    ],
    []
  );

  return (
    <div className={styles.container} ref={componentReference} tabIndex={-1}>
      <div className={styles.filterContent}>
        <div className={styles.filterRow}>
          <div className={styles.filterSelect}>
            <SelectField size="medium">
              <select
                value={filterType}
                onChange={(event) =>
                  handleFilterTypeChange(event.target.value as FilterType)
                }
              >
                {filterTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </SelectField>
          </div>
        </div>

        <div className={styles.datetimeSection}>
          <div className={styles.datetimeRow}>
            <div className={styles.dateInput}>
              <Flatpickr
                value={formatDateForDisplay(startDate)}
                onChange={handleStartDateChange}
                options={startDateOptions}
                className="flatpickr-input"
                placeholder="YYYY-MM-DD"
              />
            </div>
            <div className={styles.timeInput}>
              <Flatpickr
                value={startTime}
                onChange={handleStartTimeChange}
                options={startTimeOptions}
                className="flatpickr-input"
                placeholder="HH:MM"
              />
            </div>
          </div>
        </div>

        {filterType === 'inRange' && (
          <div className={styles.datetimeSection}>
            <div className={styles.datetimeRow}>
              <div className={styles.dateInput}>
                <Flatpickr
                  value={formatDateForDisplay(endDate)}
                  onChange={handleEndDateChange}
                  options={endDateOptions}
                  className="flatpickr-input"
                  placeholder="YYYY-MM-DD"
                />
              </div>
              <div className={styles.timeInput}>
                <Flatpickr
                  value={endTime}
                  onChange={handleEndTimeChange}
                  options={endTimeOptions}
                  className="flatpickr-input"
                  placeholder="HH:MM"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartTimeFilter;
